import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import AddIcon from '@mui/icons-material/Add';
import { FormattedMessage } from 'react-intl';
import Toolbar from '@mui/material/Toolbar';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';
import ContentView from '~/components/ContentView';
import DrawerButton from '~/containers/DrawerButton';
import { useCongregations } from '~/hooks/api/useCongregations';
import { useCircuits } from '~/hooks/api/useCircuits';
import ListItemButton from '@mui/material/ListItemButton';
import PaginationControlsWithQuery from '~/components/PaginationControls';
import messages from './messages';

const route = getRouteApi('/congregations/');

const CongregationList = () => {
  const navigate = useNavigate();
  const search = route.useSearch();
  const [page, setPage] = useState(search.page ?? 0);
  const [perPage, setPerPage] = useState(20);

  const createCongregation = () => {
    navigate({ to: '/congregations/new' });
  };

  const editCongregation = (id: number) => {
    navigate({
      to: '/congregations/$congregationId',
      params: { congregationId: String(id) },
    });
  };

  const { data: congregationsResponse, isLoading: isLoadingCongregations } =
    useCongregations({ page, perPage });
  const { data: circuitsResponse } = useCircuits();

  const circuitsById = new Map(
    (circuitsResponse?.page?.items ?? []).map((circuit) => [
      circuit.id,
      circuit,
    ])
  );

  const congregations = congregationsResponse?.data ?? [];
  const totalCount = congregationsResponse?.totalCount ?? 0;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    navigate({ to: '/congregations', search: { page: newPage } });
  };

  return (
    <ContentView
      header={
        <div>
          <AppBar position="static">
            <Toolbar>
              <DrawerButton />
              <Typography
                variant="h6"
                sx={{
                  color: 'inherit',
                }}
              >
                <FormattedMessage {...messages.title} />
              </Typography>
            </Toolbar>
          </AppBar>
        </div>
      }
      floatingActionButton={
        <Fab onClick={createCongregation} color="secondary">
          <AddIcon data-testid="create-congregation" />
        </Fab>
      }
      isLoading={isLoadingCongregations}
    >
      {!isLoadingCongregations && congregations.length === 0 ? (
        <ListItem data-test="empty-item">
          <Typography
            variant="h6"
            sx={{
              color: 'inherit',
            }}
          >
            <FormattedMessage {...messages.noCongregationsYet} />
          </Typography>
        </ListItem>
      ) : null}
      {!isLoadingCongregations ? (
        <>
          <PaginationControlsWithQuery
            totalCount={totalCount}
            isLoading={isLoadingCongregations}
            initialPage={page}
            perPage={perPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={setPerPage}
          />
          <List>
            {congregations.map((congregation) => {
              const circuit = circuitsById.get(congregation.circuitId);

              return (
                <ListItemButton
                  key={congregation.id}
                  onClick={() => editCongregation(congregation.id)}
                >
                  <ListItemText
                    primary={congregation.name}
                    secondary={
                      circuit ? (
                        <FormattedMessage
                          {...messages.name}
                          values={{ circuit: circuit.name }}
                        />
                      ) : congregation.number ? (
                        `${congregation.number}`
                      ) : undefined
                    }
                  />
                </ListItemButton>
              );
            })}
          </List>
        </>
      ) : null}
    </ContentView>
  );
};

export default CongregationList;
